import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import PropTypes from 'prop-types';
import { Field, reduxForm } from 'redux-form';
import FieldInput from '../../form/FieldInput';
import { validateEmail } from '../../form/validators';
import AccountNavLink from '../../../common/links/AccountNavLink';
import Heading from '../../../common/heading/Heading';
import Message from '../../../common/message/Message';
import Button from '../../../common/buttons/onboarding/OnboardingButton';
import { catchReduxFormSubmitRejection, validatePassword } from '../../../../helpers/validation';
import { hasValidVideolandCookie } from '../../../../helpers/authentication';
import { ROUTE_HOME, ROUTE_LOGIN } from '../../../../constants/Routes';
import {
	NEXT,
	ONBOARDING_COMPLETE_EMAIL_TITLE,
	ONBOARDING_COMPLETE_EMAIL_TITLE_WITH_TIER,
	ONBOARDING_COMPLETE_EMAIL_SUBTITLE,
	ONBOARDING_REGISTER_EMAIL_LABEL,
	ONBOARDING_REGISTER_ALREADY_ACCOUNT,
	ONBOARDING_REGISTER_LOGIN,
	BLACK_FRIDAY,
	ONBOARDING_PASSWORD_FIELD,
	ONBOARDING_COMPLETE_EMAIL_TITLE_WITH_TIER_HBO,
	ONBOARDING_COMPLETE_EMAIL_TITLE_WITH_BUNDLE,
} from '../../../../constants/Copy';
import OnboardingProgress from '../../common/onboardingProgress/OnboardingProgress.container';
import { getPresentationForTier } from '../../../../helpers/tiers';
import { useVideolandAccountState } from '../../../../hooks/useVideolandAccountState';
import useSearchParams from '../../../../hooks/useSearchParams';
import { PARAM_NAMES } from '../../../../constants/Checkout';

import '../Register.scss';
import { deviceInformation } from '../../../../helpers/device';

const validate = ({ email, password }) => {
	let errors = {};
	if (password) {
		errors.password = validatePassword({ password }).valid ? null : true;
	}

	if (email) errors = { ...errors, ...validateEmail({ email }) };
	return errors;
};

const getHeadingCopy = (tier, isBundlePreselected, isPartnerBundlePreselected) => {
	if (tier && !isPartnerBundlePreselected) {
		return isBundlePreselected 
			? ONBOARDING_COMPLETE_EMAIL_TITLE_WITH_TIER_HBO
			: ONBOARDING_COMPLETE_EMAIL_TITLE_WITH_TIER;
	}
	return isBundlePreselected && isPartnerBundlePreselected
		? ONBOARDING_COMPLETE_EMAIL_TITLE_WITH_BUNDLE
		: ONBOARDING_COMPLETE_EMAIL_TITLE;
};

export const RegisterEmail = ({
	submit,
	valid,
	pending,
	submitting,
	change,
	touch,
	history,
	handleSubmit,
	initialValues,
	tierId,
	isBundlePreselected,
	isPartnerBundlePreselected,
	isRegExperiment,
}) => {
	const hasVlCookie = hasValidVideolandCookie();
	let email = null;
	const tier = getPresentationForTier(tierId);
	const { isBlackFriday } = useVideolandAccountState();
	const { getSearchParam } = useSearchParams();
	const showBlackFridayCopy = getSearchParam(PARAM_NAMES.BLACK_FRIDAY);
	const disabled =
		!initialValues.email ||
		initialValues.email.length === 0 ||
		!!validate({ email: initialValues.email }).email ||
		(isRegExperiment &&
			(!!validate({ password: initialValues.password }).password ||
				!initialValues.password ||
				initialValues.password.length === 0));

	useEffect(() => {
		if (hasVlCookie) return history.push(ROUTE_HOME);
	}, [initialValues, email, hasVlCookie, change, history, submit, touch, valid]);

	return (
		<form
			method="POST"
			onSubmit={catchReduxFormSubmitRejection(handleSubmit)}
			className="onboarding-email-form"
		>
			<OnboardingProgress activeStep={2} />
			<Heading>
				<Message
					message={getHeadingCopy(tier, isBundlePreselected, isPartnerBundlePreselected)}
					values={{
						selected_tier:
							isBlackFriday && showBlackFridayCopy
								? `de ${BLACK_FRIDAY.TIER_LABEL} Deal`
								: tier?.title,
					}}
				/>
			</Heading>
			<p className="onboarding__text">{ONBOARDING_COMPLETE_EMAIL_SUBTITLE}</p>

			<Field
				name="email"
				element={(element) => (email = element)}
				component={FieldInput}
				type="email"
				testId="email-input"
				label={ONBOARDING_REGISTER_EMAIL_LABEL}
				autoFocus
			/>

			{isRegExperiment && (
				<Field
					name="password"
					type="password"
					label={ONBOARDING_PASSWORD_FIELD}
					component={FieldInput}
					showPassword={deviceInformation.isMobile}
					autocomplete="new-password"
					testId="password-input"
					showPasswordStrengthCheck
				/>
			)}
			<Button
				testId="email-button"
				type="submit"
				loading={pending || submitting}
				disabled={disabled}
			>
				{NEXT}
			</Button>

			<AccountNavLink>
				{ONBOARDING_REGISTER_ALREADY_ACCOUNT}{' '}
				<Link className="onboarding__link accent" tabIndex="0" to={ROUTE_LOGIN}>
					{ONBOARDING_REGISTER_LOGIN}
				</Link>
			</AccountNavLink>
		</form>
	);
};

PropTypes.RegisterEmail = {
	handleSubmit: PropTypes.func.isRequired,
	email: PropTypes.string,
	password: PropTypes.string,
	pending: PropTypes.bool,
};

export default reduxForm({
	form: 'register',
	destroyOnUnmount: false,
	touchOnChange: true,
	forceUnregisterOnUnmount: true,
	enableReinitialize: true,
	keepDirtyOnReinitialize: true,
	validate: validate,
})(RegisterEmail);
